import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';

export interface AvailableField {
  id?: string;
  lbl: string;
  val: string;
  object: string;
  group: string;
  type: number;
  field: string;
  group_sort: string;
  _canWrite?: boolean;
  _uiAccess?: {
    edit: boolean;
    delete: boolean;
    clone: boolean;
    merge: boolean;
  };
}

export interface LayoutItem {
  id: string;
  layout_id: string;
  creator_user_id: string | null;
  position: number;
  type: number;
  field: string | null;
  data: any;
  created: string;
  updated: string;
  metadata: any;
  c__creator_user_id: string | null;
  c__condition?: string;
  c__label?: string;
  c__field?: string | null;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  conditions: Array<{
    id: string;
    property: string;
    operation: string;
    value: any;
  }>;
  _has_detail: boolean;
  i__required: boolean;
  i__requiredResolve: boolean;
  i__readOnly: boolean;
  i__hasCondition: boolean;
  i__conditionAction: string;
  i__conditionMatches: string;
  _canWrite: boolean;
  _uiAccess: {
    edit: boolean;
    delete: boolean;
    clone: boolean;
    merge: boolean;
  };
}

export interface SchemaField {
  layout_id: string;
  creator_user_id: string | null;
  position: number;
  type: number;
  field: string;
  data: any;
  created: string;
  updated: string;
  metadata: any;
  c__creator_user_id: string | null;
  c__condition: string;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  conditions: any[];
  object: string;
  fieldType?: string;
  decimalPlaces?: number;
  required: boolean;
  requiredResolve: boolean;
  readOnly: boolean;
  lbl: string;
  options?: Array<{
    id: string;
    lbl: string;
    val: string;
    default: boolean;
  }>;
}

export interface CMSLayout {
  id: string;
  partners_id: string;
  creator_user_id: string | null;
  object: string;
  title: string;
  description: string | null;
  data: any;
  created: string;
  updated: string;
  metadata: any;
  c__creator_user_id: string | null;
  c__description: string | null;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  _has_detail: boolean;
  isTemplate: boolean;
  c__d_object: string;
  schemaFields: SchemaField[];
  _canWrite: boolean;
  _uiAccess: {
    edit: boolean;
    delete: boolean;
    clone: boolean;
    merge: boolean;
  };
  previewObjectId?: string;
}

export interface CardField {
  id?: string;
  card_id: string;
  creator_user_id: string | null;
  position: number;
  type: number;
  field: string;
  data: any;
  created: string;
  updated: string;
  metadata: any;
  c__creator_user_id: string | null;
  c__condition: string;
  c__label?: string;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  conditions: any[];
  object?: string;
  fieldType?: string;
  lbl?: string;
  _has_detail?: boolean;
  i__hasCondition?: boolean;
  i__conditionAction?: string;
  i__conditionMatches?: string;
  _canWrite?: boolean;
  _uiAccess?: {
    edit: boolean;
    delete: boolean;
    clone: boolean;
    merge: boolean;
  };
}

export interface CMSCard {
  id: string;
  partners_id: string;
  creator_user_id: string | null;
  object: string;
  type: number;
  title: string;
  description: string | null;
  data: any;
  created: string | null;
  updated: string | null;
  metadata: any;
  c__d_type: string;
  c__key: string;
  c__creator_user_id?: string | null;
  c__description: string | null;
  c__d_object: string;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  _has_detail: boolean;
  isTemplate: boolean;
  cardFields: CardField[];
  _canWrite: boolean;
  _uiAccess: {
    edit: boolean;
    delete: boolean;
    clone: boolean;
    merge: boolean;
  };
}

export interface CMSLayoutsResponse {
  success: boolean;
  message?: string;
  partners_layouts?: {
    results: CMSLayout[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface LayoutItemsResponse {
  success: boolean;
  message?: string;
  partners_layouts_items?: {
    results: LayoutItem[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface AvailableFieldsResponse {
  success: boolean;
  message?: string;
  available_items?: {
    results: AvailableField[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface CMSCardsResponse {
  success: boolean;
  message?: string;
  partners_cards?: {
    results: CMSCard[];
    totalCount: number;
  };
  meta?: {
    maxFields: number;
  };
  current_server_time: string;
}

export interface CardItemsResponse {
  success: boolean;
  message?: string;
  partners_cards_items?: {
    results: CardField[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface CardAvailableFieldsResponse {
  success: boolean;
  message?: string;
  available_items?: {
    results: AvailableField[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface ObjectField {
  id: string;
  object: string;
  field: string;
  lbl: string;
  type: string;
  fieldType: string;
  isCheckbox: boolean;
  isNumeric: boolean;
  isPicklist: boolean;
  isPicklistMulti: boolean;
  options: Array<{
    id: string;
    lbl: string;
    val: string;
    default?: boolean;
  }>;
}

export interface ObjectFieldsResponse {
  success: boolean;
  message?: string;
  object_fields?: {
    results: ObjectField[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface FetchLayoutsParams {
  page?: number;
  start?: number;
  limit?: number;
  filter?: Array<{
    property: string;
    value: string;
  }>;
}

export interface FetchLayoutItemsParams {
  page?: number;
  start?: number;
  limit?: number;
  sort?: Array<{
    property: string;
    direction: string;
  }>;
  filter?: Array<{
    property: string;
    value: string;
  }>;
}

/**
 * Composable that provides access to CMS API functions.
 */
export function useCMSAPI() {
  const authStore = useAuthStore();
  const httpClient = getHttpClient(authStore);

  return {
    async fetchLayouts(params: FetchLayoutsParams = {}): Promise<{ results: CMSLayout[]; totalCount: number }> {
      const data = await httpClient.get<CMSLayoutsResponse>('admin/v4/cms/', {
        'sAction': 'layoutsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '25',
        'filter': JSON.stringify(params.filter || [
          { property: 'id', value: '_no_filter_' },
          { property: 'partners_id', value: '_no_filter_' },
          { property: 'object', value: '_no_filter_' }
        ])
      });
      
      if (data.success && data.partners_layouts?.results) {
        return {
          results: data.partners_layouts.results,
          totalCount: data.partners_layouts.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch CMS layouts');
    },

    async fetchLayoutItems(params: FetchLayoutItemsParams = {}): Promise<{ results: LayoutItem[]; totalCount: number }> {
      const data = await httpClient.get<LayoutItemsResponse>('admin/v4/cms/', {
        'sAction': 'layoutItemsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '200',
        'sort': JSON.stringify(params.sort || [{ property: 'position', direction: 'ASC' }]),
        'filter': JSON.stringify(params.filter || [])
      });
      
      if (data.success && data.partners_layouts_items?.results) {
        return {
          results: data.partners_layouts_items.results,
          totalCount: data.partners_layouts_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch layout items');
    },

    async fetchAvailableFields(params: FetchLayoutsParams = {}): Promise<{ results: AvailableField[]; totalCount: number }> {
      const data = await httpClient.get<AvailableFieldsResponse>('admin/v4/cms/', {
        'sAction': 'layoutAvailItemsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '1000',
        'filter': JSON.stringify(params.filter || [])
      });
      
      if (data.success && data.available_items?.results) {
        return {
          results: data.available_items.results,
          totalCount: data.available_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch available fields');
    },

    async saveLayoutItems(items: LayoutItem[]): Promise<{ results: LayoutItem[]; totalCount: number }> {
      // Prepare items for API - remove temporary IDs and clean up data
      const itemsToSave = items.map(item => {
        const cleanItem: any = {
          layout_id: item.layout_id,
          field: item.field,
          c__label: item.c__label,
          position: item.position,
          type: item.type,
          data: item.data || {},
          conditions: item.conditions || [],
          i__hasCondition: item.i__hasCondition || false,
          i__required: item.i__required || false,
          i__requiredResolve: item.i__requiredResolve || false,
          i__readOnly: item.i__readOnly || false
        };
        
        // Only include ID if it's not a temporary one
        if (item.id && !item.id.startsWith('temp-')) {
          cleanItem.id = item.id;
        }
        
        return cleanItem;
      });

      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_layouts_items', JSON.stringify(itemsToSave));

      const data = await httpClient.post<LayoutItemsResponse>('admin/v4/cms/', formData, {
        sAction: 'layoutItemsPut'
      });
      
      if (data.success && data.partners_layouts_items?.results) {
        return {
          results: data.partners_layouts_items.results,
          totalCount: data.partners_layouts_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to save layout items');
    },

    async deleteLayoutItems(items: Array<{ id: string; conditions: any[] }>): Promise<{ success: boolean }> {
      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('limit', '200');
      formData.append('partners_layouts_items', JSON.stringify(items));

      const data = await httpClient.post<{ success: boolean; current_server_time: string }>(
        'admin/v4/cms/', 
        formData, 
        {
          sAction: 'layoutItemsDelete'
        }
      );
      
      if (data.success) {
        return { success: true };
      }
      
      throw new Error('Failed to delete layout items');
    },

    async customizeTemplate(layout: CMSLayout): Promise<CMSLayout> {
      // Prepare layout for customization - set createDefaults flag
      // Omit id field to let the server generate a new UUID for the layout
      const layoutData: any = {
        partners_id: layout.partners_id,
        object: layout.object,
        title: layout.title,
        createDefaults: true,
        creator_user_id: '',
        description: '',
        status: 0,
        created: null,
        updated: null,
        _canWrite: layout._canWrite,
        _uiAccess: layout._uiAccess
      };

      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_layouts', JSON.stringify([layoutData]));

      const data = await httpClient.post<CMSLayoutsResponse>('admin/v4/cms/', formData, {
        sAction: 'layoutsPut'
      });
      
      if (data.success && data.partners_layouts?.results && data.partners_layouts.results.length > 0) {
        return data.partners_layouts.results[0];
      }
      
      throw new Error(data.message || 'Failed to customize template');
    },

    async customizeCardTemplate(card: CMSCard): Promise<CMSCard> {
      // Prepare card for customization - set createDefaults flag.
      // Intentionally omit `id` so the server generates a fresh UUID for the new
      // custom card instead of reusing the slug-based template ID (e.g. "issues-1").
      // Sending the slug ID causes the server to update-in-place and accumulate items
      // across multiple reset+customize cycles. Without an id, the server creates a
      // proper UUID card that cascades correctly on subsequent deletes.
      const cardData = {
        partners_id: card.partners_id,
        object: card.object,
        type: card.type,
        title: card.title,
        createDefaults: true,
        creator_user_id: card.creator_user_id || '',
        description: card.description || '',
        status: 0,
        created: card.created,
        updated: card.updated,
        _canWrite: card._canWrite,
        _uiAccess: card._uiAccess
      };

      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_cards', JSON.stringify([cardData]));

      const data = await httpClient.post<CMSCardsResponse>('admin/v4/cms/', formData, {
        sAction: 'cardsPut'
      });
      
      if (data.success && data.partners_cards?.results && data.partners_cards.results.length > 0) {
        return data.partners_cards.results[0];
      }
      
      throw new Error(data.message || 'Failed to customize card template');
    },

    async touchCard(card: CMSCard): Promise<void> {
      // Re-submit the card's own metadata via cardsPut (with its UUID) to bring it back
      // into a proper custom-card state on the server. After all items are deleted the
      // server may internally mark the card as template-state, which causes a single
      // new cardItemsPut to be denied. Touching the card first reactivates it.
      const cardData = {
        id: card.id,
        partners_id: card.partners_id,
        object: card.object,
        type: card.type,
        title: card.title,
        creator_user_id: card.creator_user_id || '',
        description: card.description || '',
        status: 0,
        created: card.created,
        updated: card.updated,
        _canWrite: card._canWrite,
        _uiAccess: card._uiAccess
      };

      const formData = new URLSearchParams();
      formData.append('partners_cards', JSON.stringify([cardData]));

      await httpClient.post<CMSCardsResponse>('admin/v4/cms/', formData, {
        sAction: 'cardsPut'
      });
    },

    async fetchCards(params: FetchLayoutsParams = {}): Promise<{ results: CMSCard[]; totalCount: number; maxFields?: number }> {
      const data = await httpClient.get<CMSCardsResponse>('admin/v4/cms/', {
        'sAction': 'cardsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '25',
        'filter': JSON.stringify(params.filter || [
          { property: 'id', value: '_no_filter_' },
          { property: 'partners_id', value: '_no_filter_' },
          { property: 'object', value: '_no_filter_' }
        ])
      });
      
      if (data.success && data.partners_cards?.results) {
        return {
          results: data.partners_cards.results,
          totalCount: data.partners_cards.totalCount || 0,
          maxFields: data.meta?.maxFields
        };
      }
      
      throw new Error(data.message || 'Failed to fetch CMS cards');
    },

    async fetchCardItems(params: FetchLayoutItemsParams = {}): Promise<{ results: CardField[]; totalCount: number }> {
      const data = await httpClient.get<CardItemsResponse>('admin/v4/cms/', {
        'sAction': 'cardItemsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '200',
        'sort': JSON.stringify(params.sort || [{ property: 'position', direction: 'ASC' }]),
        'filter': JSON.stringify(params.filter || [])
      });
      
      if (data.success && data.partners_cards_items?.results) {
        return {
          results: data.partners_cards_items.results,
          totalCount: data.partners_cards_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch card items');
    },

    async fetchCardAvailableFields(params: FetchLayoutsParams = {}): Promise<{ results: AvailableField[]; totalCount: number }> {
      const data = await httpClient.get<CardAvailableFieldsResponse>('admin/v4/cms/', {
        'sAction': 'cardAvailItemsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '1000',
        'filter': JSON.stringify(params.filter || [])
      });
      
      if (data.success && data.available_items?.results) {
        return {
          results: data.available_items.results,
          totalCount: data.available_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch card available fields');
    },

    async saveCardItems(items: CardField[]): Promise<{ results: CardField[]; totalCount: number }> {
      // Prepare items for API - remove temporary IDs and clean up data
      const itemsToSave = items.map(item => {
        const cleanItem: any = {
          card_id: item.card_id,
          field: item.field,
          c__label: item.c__label,
          position: item.position,
          type: item.type
        };
        
        const isTemplateItemId = item.id?.startsWith('settings.CardItems-') || item.id?.startsWith('settings.LayoutItems-');
        const isExistingItem = item.id && !item.id.startsWith('temp-') && !isTemplateItemId;

        // Always include the ID unless it's a client-side temp placeholder
        if (item.id && !item.id.startsWith('temp-')) {
          cleanItem.id = item.id;
        }

        if (isExistingItem) {
          // Updating an existing DB item — send back its server-managed fields
          if (item.creator_user_id !== undefined) cleanItem.creator_user_id = item.creator_user_id;
          if (item.created) cleanItem.created = item.created;
          if (item.updated) cleanItem.updated = item.updated;
        } else if (isTemplateItemId) {
          // New item referenced by a template ID (e.g. "settings.CardItems-2").
          // The server uses this to create a fresh UUID item; send the same shape
          // the working app sends so the server accepts the create.
          cleanItem.creator_user_id = '';
          cleanItem.created = null;
          cleanItem.updated = null;
          cleanItem.conditions = item.conditions || [];
          if (item._canWrite !== undefined) cleanItem._canWrite = item._canWrite;
          if (item._uiAccess) cleanItem._uiAccess = item._uiAccess;
        }
        
        return cleanItem;
      });

      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_cards_items', JSON.stringify(itemsToSave));

      const data = await httpClient.post<CardItemsResponse>(
        'admin/v4/cms/', 
        formData, 
        {
          sAction: 'cardItemsPut'
        }
      );
      
      if (data.success && data.partners_cards_items?.results) {
        return {
          results: data.partners_cards_items.results,
          totalCount: data.partners_cards_items.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to save card items');
    },

    async deleteCardItems(items: Array<{ id: string; conditions: any[] }>): Promise<{ success: boolean }> {
      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_cards_items', JSON.stringify(items));

      const data = await httpClient.post<{ success: boolean; current_server_time: string }>(
        'admin/v4/cms/', 
        formData, 
        {
          sAction: 'cardItemsDelete'
        }
      );
      
      if (data.success) {
        return { success: true };
      }
      
      throw new Error('Failed to delete card items');
    },

    async deleteCard(cardId: string): Promise<{ success: boolean }> {
      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_cards', JSON.stringify([{ id: cardId }]));

      const data = await httpClient.post<{ success: boolean; current_server_time: string }>(
        'admin/v4/cms/', 
        formData, 
        {
          sAction: 'cardsDelete'
        }
      );
      
      if (data.success) {
        return { success: true };
      }
      
      throw new Error('Failed to delete card');
    },

    async deleteLayout(layoutId: string): Promise<{ success: boolean }> {
      // Build URL-encoded form data
      const formData = new URLSearchParams();
      formData.append('partners_layouts', JSON.stringify([{ id: layoutId }]));

      const data = await httpClient.post<{ success: boolean; current_server_time: string }>(
        'admin/v4/cms/', 
        formData, 
        {
          sAction: 'layoutsDelete'
        }
      );
      
      if (data.success) {
        return { success: true };
      }
      
      throw new Error('Failed to delete layout');
    },

    async fetchObjectFields(params: FetchLayoutsParams = {}): Promise<{ results: ObjectField[]; totalCount: number }> {
      const data = await httpClient.get<ObjectFieldsResponse>('admin/v4/cms/', {
        'sAction': 'objectFieldsListing',
        'page': params.page?.toString() || '1',
        'start': params.start?.toString() || '0',
        'limit': params.limit?.toString() || '1000',
        'filter': JSON.stringify(params.filter || [])
      });
      
      if (data.success && data.object_fields?.results) {
        return {
          results: data.object_fields.results,
          totalCount: data.object_fields.totalCount || 0
        };
      }
      
      throw new Error(data.message || 'Failed to fetch object fields');
    }
  };
} 